// Why the persisted cache must lose the FIRST render to the document, and why ordering alone is not
// enough: docs/notes/app/query-persistence.md.

/** The shape `persistQueryClient` hands to `hydrate`: enough of it to filter, and nothing more. */
interface PersistedQuery {
  queryHash: string
}

/** A persisted entry read for its freshness rather than only its identity. */
interface TimestampedQuery extends PersistedQuery {
  state: { dataUpdatedAt: number }
}

/**
 * Whether a query may live in the persisted cache. The assistant's (`agent.*`) never does: its conversation names
 * people and their profiles, and a restored copy would replay a conversation the server has since moved past.
 */
export function isPersistable(query: { queryKey: readonly unknown[] }): boolean {
  const path = query.queryKey[0]
  return !(Array.isArray(path) && path[0] === 'agent')
}

/**
 * The persisted entries that must NOT be restored, because this document already carries the query.
 *
 * Keyed on presence, not on freshness: a document-carried query is authoritative whatever its state, and
 * comparing timestamps here reintroduces the `dataUpdatedAt: 0` trap this exists to avoid.
 */
export function withoutDocumentQueries<T extends PersistedQuery>(
  persisted: readonly T[],
  documentCarries: (queryHash: string) => boolean,
): T[] {
  return persisted.filter((query) => !documentCarries(query.queryHash))
}

/**
 * When this document was rendered, read off the queries it carries.
 *
 * Only a query the server RESOLVED can date it — a pending one is stamped when its promise settles in the
 * browser — so `undefined` (no refetch) is the honest answer rather than a guess.
 */
export function documentRenderedAt(
  documentQueries: Iterable<{ state: { dataUpdatedAt: number } }>,
): number | undefined {
  let newest: number | undefined
  for (const query of documentQueries) {
    const at = query.state.dataUpdatedAt
    if (at > 0 && (newest === undefined || at > newest)) newest = at
  }
  return newest
}

/**
 * The dropped entries whose persisted copy was genuinely newer than the document that displaced it — the
 * visitor's whole loss from the document-first rule, refetched once the tree is live.
 *
 * Compared against `renderedAt` rather than the displaced entry's own timestamp: a pending document query
 * sits at `0`, so every persisted copy would look newer and a fresh load would refetch its whole cache.
 */
export function supersededDocumentQueries(
  persisted: readonly TimestampedQuery[],
  documentCarries: (queryHash: string) => boolean,
  renderedAt: number | undefined,
): string[] {
  if (renderedAt === undefined) return []
  return persisted
    .filter((query) => documentCarries(query.queryHash) && query.state.dataUpdatedAt > renderedAt)
    .map((query) => query.queryHash)
}

/**
 * Run `restore` once the document has finished parsing — the FALLBACK trigger, for a document with no SSR
 * payload at all. `DOMContentLoaded` is the signal because a payload, when there is one, travels in inline
 * `<script>` tags at the end of `<body>` and inline scripts block the parser.
 */
export function afterSsrPayloadApplied(restore: () => void): void {
  if (typeof document === 'undefined') return
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => restore(), { once: true })
    return
  }
  restore()
}
